// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 63.22.0 (decisions 1–2, OD-A7) — the Forseti star, on every admin page.
 *
 * Mounted ONCE, in `AdminLayout`. It renders only when the user holds
 * `AI_ADVISOR_CHAT` and the organisation has the Forseti module enabled — the
 * same pair every chat endpoint and chat route requires — and never on the
 * Forseti chat pages themselves, where it would open a chat on top of a chat.
 *
 * The button does not position itself: it occupies a slot in the
 * `BottomRightDock`, which owns the corner and the toast clearance.
 *
 * Ctrl+Shift+F (⌘+Shift+F on macOS) opens the same modal, except while the
 * operator is typing or another modal is open (`useGlobalShortcut`). Focus is
 * moved to the star first, so closing the modal returns it there
 * (`AccessibleModal` restores focus to the element that had it on open).
 */

import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Permission } from '@vn-grc/shared';
import { useLocale } from '../../hooks/useLocale.js';
import { usePermission } from '../../hooks/usePermission.js';
import { useGlobalShortcut } from '../../hooks/useGlobalShortcut.js';
import { useModuleConfig } from '../../contexts/ModuleConfigContext.js';
import { useForsetiPageRecord } from '../../contexts/ForsetiPageContext.js';
import { BottomRightDockSlot } from '../layout/BottomRightDock.js';
import { ForsetiIcon } from '../icons/ActionIcons.js';

/**
 * Loaded on first open. The modal carries the chat engine and every record
 * workflow view; none of that belongs in the layout chunk every admin page
 * downloads.
 */
const AskForsetiModal = lazy(() =>
  import('../../pages/admin/forseti/components/AskForsetiModal.js').then((m) => ({
    default: m.AskForsetiModal,
  })),
);

/** The module toggle the chat surface is gated on. */
export const FORSETI_MODULE_ID = 'forseti';
/** Launcher dock order: the star sits at the corner, occupants added later sit to its left. */
export const FORSETI_LAUNCHER_DOCK_ORDER = 100;
export const FORSETI_SHORTCUT = { code: 'KeyF', shift: true } as const;

/**
 * The Forseti chat pages, where the launcher is not rendered: the overview
 * (`/admin/forseti`, and its `/admin/ai-advisor` alias) and every chat.
 */
export function isForsetiChatRoute(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '');
  return (
    path === '/admin/forseti' ||
    path === '/admin/ai-advisor' ||
    path.startsWith('/admin/forseti/chats/') ||
    path === '/admin/forseti/chats'
  );
}

export function ForsetiLauncher(): JSX.Element | null {
  const { t } = useLocale();
  const labels = t.forseti.launcher;
  const canChat = usePermission(Permission.AI_ADVISOR_CHAT);
  const { isModuleEnabled } = useModuleConfig();
  const { pathname } = useLocation();
  const pageRecord = useForsetiPageRecord();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const visible = canChat && isModuleEnabled(FORSETI_MODULE_ID) && !isForsetiChatRoute(pathname);

  // Hidden means closed. Without this the modal unmounted with the star (on a
  // chat route, or when the permission or module went away) but `open` stayed
  // true, so it re-opened by itself on the next ordinary page (gate E12.5
  // pass 1, P2-1).
  useEffect(() => {
    if (!visible) setOpen(false);
  }, [visible]);

  // Focus the star BEFORE opening: `AccessibleModal` returns focus to whatever
  // held it on open, and a pointer click does not focus a button in every
  // browser, so without this a mouse user's focus went back to <body>.
  const openModal = () => {
    buttonRef.current?.focus();
    setOpen(true);
  };

  useGlobalShortcut(FORSETI_SHORTCUT, openModal, visible && !open);

  if (!visible) return null;

  return (
    <>
      <BottomRightDockSlot id="forseti-launcher" order={FORSETI_LAUNCHER_DOCK_ORDER}>
        <button
          ref={buttonRef}
          type="button"
          className="dm-forseti-launcher"
          aria-label={labels.openLabel}
          aria-describedby="forseti-launcher-hint"
          aria-keyshortcuts="Control+Shift+F Meta+Shift+F"
          aria-haspopup="dialog"
          aria-expanded={open}
          title={labels.tooltip}
          onClick={openModal}
          data-testid="forseti-launcher"
        >
          <ForsetiIcon size={22} />
        </button>
        <span id="forseti-launcher-hint" className="sr-only">
          {labels.tooltip}
        </span>
      </BottomRightDockSlot>
      {open && (
        <Suspense fallback={null}>
          <AskForsetiModal open onClose={() => setOpen(false)} pageRecord={pageRecord} />
        </Suspense>
      )}
    </>
  );
}
